/**
 * Pure logic for the post-upload transcoding poll (UploadContext.startPolling).
 *
 * The poll fetches GET /api/videos/:id, which returns the video object BARE (the `status`
 * field is top-level — one of the video_status enum values uploading|processing|ready|failed).
 * The poll previously read `body.data?.status`, assuming a { data } wrapper the endpoint never
 * sends, so `status` was always undefined and the poll never resolved — the upload UI stayed
 * stuck on "processing" and polled forever. This module reads the bare `status` and maps the
 * terminal states to a job-phase transition; non-terminal/unknown states return null (keep
 * polling). Extracted so the decision is unit-testable without fetch/timer mocks.
 */

export interface UploadPollResult {
  phase: 'ready' | 'error';
  errorMessage: string | null;
}

/**
 * From a GET /api/videos/:id response body (the video, BARE), decide the job-phase transition:
 *   - status 'ready'  → { phase: 'ready',  errorMessage: null }
 *   - status 'failed' → { phase: 'error',  errorMessage: 'Transcoding failed' }
 *   - anything else (uploading/processing/unknown/missing) → null (still processing, keep polling)
 */
export function resolveVideoPollResult(body: unknown): UploadPollResult | null {
  const status =
    body !== null && typeof body === 'object'
      ? (body as { status?: unknown }).status
      : undefined;

  if (status === 'ready') return { phase: 'ready', errorMessage: null };
  if (status === 'failed') return { phase: 'error', errorMessage: 'Transcoding failed' };
  return null;
}
